// How Locked behaves, reachable only from inside it while it is open: changing how it hides should
// take having proved it can be opened.
//
// Hidden, Locked leaves the sidebar while sealed, its storage stops being told apart, and the only
// way back in is its passphrase typed into the search box, which the dialog says plainly as the
// switch is turned on. The reseal interval starts over at the new length straight away. The
// passphrase changes only with the current one: one that is forgotten can't be changed, only reset.
import PropTypes from 'prop-types'
import {useMutation} from '@apollo/client/react'
import {useState} from 'react'

import Dialog from '@mui/material/Dialog'
import SettingsIcon from '@mui/icons-material/SettingsOutlined'
import Switch from '@mui/material/Switch'

import PassphraseField from './PassphraseField'
import {BaseButton} from '../shared/blocks/Buttons'
import {Text} from '../shared/blocks/Texts'

import {BAD_REQUEST, says} from '../../lib/errorTools'
import {CHANGE_PASSPHRASE, UPDATE_LOCKED_SETTINGS} from '../../graphql/mutations/locked'
import {SHORTEST_PASSPHRASE} from './PassphraseDialog'
import {changeResealAfter} from '../../lib/lockedTools'

import './lockedDialog.scss'

const RESEAL_CHOICES = [
  {seconds: 60, label: '1 minute'},
  {seconds: 300, label: '5 minutes'},
  {seconds: 900, label: '15 minutes'}
]

const LockedSettingsDialog = ({preferences, onClose, onSaved}) => {
  const [hidden, setHidden] = useState(preferences.lockedHidden)
  const [resealAfter, setResealAfter] = useState(preferences.lockedResealAfter)
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [failure, setFailure] = useState(null)
  const [updateSettings, {loading: saving}] = useMutation(UPDATE_LOCKED_SETTINGS)
  const [changePassphrase, {loading: changing}] = useMutation(CHANGE_PASSPHRASE)
  const changingPassphrase = Boolean(current || next)
  const nextTooShort = changingPassphrase && next.length < SHORTEST_PASSPHRASE

  const handleSubmit = async event => {
    event.preventDefault()
    if (nextTooShort) return

    setFailure(null)

    try {
      await updateSettings({variables: {input: {hidden, resealAfter}}})
      changeResealAfter(resealAfter)
    } catch {
      return setFailure('settings')
    }

    if (changingPassphrase) {
      try {
        await changePassphrase({variables: {input: {current, 'new': next}}})
      } catch (error) {
        return setFailure(says(error, BAD_REQUEST) ? 'wrong' : 'passphrase')
      }
    }

    onSaved({passphraseChanged: changingPassphrase})
  }

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby='locked-settings-title'
      slotProps={{paper: {className: 'locked-dialog', component: 'form', onSubmit: handleSubmit}}}>
      <div className='dialog-head'>
        <SettingsIcon />
        <h2 className='dialog-title' id='locked-settings-title'>Locked settings</h2>
      </div>

      <div className='setting'>
        <div>
          <div className='setting-name' id='hide-locked-label'>Hide from the sidebar</div>
          <div className='setting-description'>
            While sealed, Locked leaves the sidebar entirely and its storage stops being itemised. It reappears whenever it is open.
          </div>
        </div>
        <Switch
          color='secondary'
          checked={hidden}
          onChange={event => setHidden(event.target.checked)}
          slotProps={{input: {'aria-labelledby': 'hide-locked-label'}}}
        />
      </div>
      {hidden && (
        <Text className='dialog-warning'>
          With this on, the only way back in is to type your passphrase into the search box on any screen. There is no
          other entrance — write the passphrase down somewhere safe first.
        </Text>
      )}

      <div className='setting'>
        <div>
          <div className='setting-name' id='reseal-after-label'>Reseal after</div>
          <div className='setting-description'>Idle time before Locked closes itself.</div>
        </div>
        <select
          aria-labelledby='reseal-after-label'
          value={resealAfter}
          onChange={event => setResealAfter(Number(event.target.value))}>
          {RESEAL_CHOICES.map(({seconds, label}) => <option key={seconds} value={seconds}>{label}</option>)}
        </select>
      </div>

      <div className='setting'>
        <div>
          <div className='setting-name'>Passphrase</div>
          <div className='setting-description'>Change it with the one you have now. Everything in Locked stays as it is.</div>
        </div>
      </div>
      <PassphraseField label='Current passphrase' value={current} onChange={setCurrent} autoComplete='current-password' />
      <PassphraseField
        label='New passphrase'
        value={next}
        onChange={setNext}
        autoComplete='new-password'
        hint={`At least ${SHORTEST_PASSPHRASE} characters. Any phrase you like — length beats punctuation.`}
      />

      {failure === 'wrong' && <Text className='dialog-error' role='alert'>That is not your current passphrase.</Text>}
      {failure === 'passphrase' && <Text className='dialog-error' role='alert'>The passphrase couldn’t be changed. Try again.</Text>}
      {failure === 'settings' && <Text className='dialog-error' role='alert'>The settings couldn’t be saved. Try again.</Text>}

      <div className='dialog-actions'>
        <BaseButton className='dialog-cancel' variant='text' onClick={onClose}>Cancel</BaseButton>
        <BaseButton className='dialog-confirm' color='secondary' type='submit' disabled={saving || changing || nextTooShort}>
          Save
        </BaseButton>
      </div>
    </Dialog>
  )
}

LockedSettingsDialog.propTypes = {
  preferences: PropTypes.shape({
    lockedHidden: PropTypes.bool.isRequired,
    lockedResealAfter: PropTypes.number.isRequired
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  // Handed whether the passphrase was changed along with the settings.
  onSaved: PropTypes.func.isRequired
}

export default LockedSettingsDialog
